import React, { forwardRef, useEffect, useState } from "react";
import { SOCIAL_LINKS } from "../../config";
import "./AuthShowcase.css";
import { MARKETING_URL } from "../../config";

// What Socialyorix does, shown as compact highlights beside the form.
const highlights = [
  ["ti-sparkles", "AI Post Studio"],
  ["ti-calendar-event", "Content Calendar"],
  ["ti-message-circle", "Inbox & Automations"],
  ["ti-chart-bar", "Leads & Analytics"]
];
const supportedPlatforms = [
  { name: "Instagram", icon: "ti-brand-instagram" },
  { name: "Facebook", icon: "ti-brand-facebook" },
  { name: "LinkedIn", icon: "ti-brand-linkedin" },
  { name: "X", icon: "ti-brand-x" },
  { name: "YouTube", icon: "ti-brand-youtube" }
];

// Links to Socialyorix's own profiles when configured; otherwise the platforms the app publishes to.
const SocialRow = () => SOCIAL_LINKS.length
  ? <nav className="auth-social" aria-label="Follow Socialyorix">
    <span>Follow Socialyorix</span>
    <div>{SOCIAL_LINKS.map((link) => <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer" aria-label={`Socialyorix on ${link.name} (opens in a new tab)`} title={link.name}><i className={`ti ${link.icon}`} aria-hidden="true" /></a>)}</div>
  </nav>
  : <div className="auth-social is-platforms">
    <span>Publish to</span>
    <div>{supportedPlatforms.map((platform) => <span key={platform.name} title={platform.name}><i className={`ti ${platform.icon}`} aria-hidden="true" /><span className="visually-hidden">{platform.name}</span></span>)}</div>
  </div>;

// Split-screen frame shared by the sign-in and registration pages.
const AuthShowcase = ({ eyebrow, title, intro, children }) => <main className="auth-shell">
  <aside className="auth-showcase">
    <div className="auth-glow auth-glow-one" aria-hidden="true" />
    <div className="auth-glow auth-glow-two" aria-hidden="true" />

    <div className="auth-showcase-copy">
      <a href={MARKETING_URL} className="auth-brand" aria-label="Socialyorix home">
        <span className="auth-brand-name"><img className="auth-brand-logo" src={`${process.env.PUBLIC_URL}/assets/images/logo/socialyorix-logo-white.png`} alt="" /></span>
      </a>
      <span className="auth-chip"><i className="ti ti-sparkles" aria-hidden="true" /> {eyebrow}</span>
      <h1>{title}</h1>
      <p>{intro}</p>
      <ul className="auth-highlights" aria-label="What you can do in Socialyorix">
        {highlights.map(([icon, label]) => <li key={label}><i className={`ti ${icon}`} aria-hidden="true" /> {label}</li>)}
      </ul>
    </div>

    <SocialRow />
  </aside>

  <section className="auth-panel">
    <div className="auth-card">{children}</div>
  </section>
</main>;

// Validation shared by the auth screens; the API enforces the same rules.
export const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const validateEmail = (value) => {
  const email = value.trim();
  if (!email) return "Enter your email address.";
  if (email.length > 254 || !emailPattern.test(email)) return "Enter a valid email address, like you@business.com.";
  return "";
};
export const passwordMeetsMinimum = (value) => value.length >= 10 && value.length <= 72 && /[A-Za-z]/.test(value) && /\d/.test(value);
export const validateNewPassword = (password, confirmPassword) => ({
  password: !password ? "Create a password." : !passwordMeetsMinimum(password) ? "Use 10 to 72 characters with at least one letter and one number." : "",
  confirmPassword: !confirmPassword ? "Confirm your password." : confirmPassword !== password ? "Passwords do not match." : ""
});

const describedByIds = (...ids) => ids.filter(Boolean).join(" ") || undefined;
const FieldMessages = ({ id, hint, error }) => <>
  {hint && !error && <small id={`${id}-hint`} className="auth-hint">{hint}</small>}
  {error && <small id={`${id}-error`} className="auth-field-error" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /> {error}</small>}
</>;

// Password input with a show/hide toggle and a Caps Lock warning.
export const PasswordField = ({ id, label, value, onChange, onBlur, autoComplete, describedBy, error, hint, ...inputProps }) => {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const checkCaps = (event) => setCapsLock(Boolean(event.getModifierState?.("CapsLock")));
  return <div className={`auth-field${error ? " has-error" : ""}`}>
    <label htmlFor={id}>{label}</label>
    <div className="auth-input">
      <i className="ti ti-lock auth-input-icon" aria-hidden="true" />
      <input id={id} type={visible ? "text" : "password"} autoComplete={autoComplete} value={value} onChange={onChange}
        onKeyUp={checkCaps} onKeyDown={checkCaps} onBlur={(event) => { setCapsLock(false); onBlur?.(event); }}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByIds(describedBy, hint && !error ? `${id}-hint` : null, error ? `${id}-error` : null, capsLock ? `${id}-caps` : null)}
        {...inputProps} />
      <button type="button" className="auth-reveal" onClick={() => setVisible((current) => !current)} aria-pressed={visible} aria-controls={id} title={visible ? "Hide password" : "Show password"}>
        <i className={`ti ${visible ? "ti-eye-off" : "ti-eye"}`} aria-hidden="true" /><span className="visually-hidden">{visible ? "Hide password" : "Show password"}</span>
      </button>
    </div>
    {capsLock && <small id={`${id}-caps`} className="auth-caps" role="status"><i className="ti ti-arrow-big-up-line" aria-hidden="true" /> Caps Lock is on</small>}
    <FieldMessages id={id} hint={hint} error={error} />
  </div>;
};

export const TextField = forwardRef(({ id, label, icon, error, hint, ...inputProps }, ref) => <div className={`auth-field${error ? " has-error" : ""}`}>
  <label htmlFor={id}>{label}</label>
  <div className="auth-input">
    {icon && <i className={`ti ${icon} auth-input-icon`} aria-hidden="true" />}
    <input ref={ref} id={id} aria-invalid={error ? true : undefined} aria-describedby={describedByIds(hint && !error ? `${id}-hint` : null, error ? `${id}-error` : null)} {...inputProps} />
  </div>
  <FieldMessages id={id} hint={hint} error={error} />
</div>);
TextField.displayName = "TextField";

export const SubmitButton = ({ busy, busyLabel, children, ...props }) => <button className="auth-submit" type="submit" disabled={busy} aria-busy={busy || undefined} {...props}>
  {busy ? <><span className="auth-spinner" aria-hidden="true" /> {busyLabel}</> : <>{children} <i className="ti ti-arrow-right" aria-hidden="true" /></>}
</button>;

const strengthLabels = ["", "Weak", "Fair", "Good", "Strong"];
// Score 0-4: length, a letter, a number, and mixed case or a symbol.
export const passwordStrength = (value) => !value ? 0 : [
  value.length >= 10, /[A-Za-z]/.test(value), /\d/.test(value), (/[a-z]/.test(value) && /[A-Z]/.test(value)) || /[^A-Za-z0-9]/.test(value)
].filter(Boolean).length;

export const PasswordStrength = ({ id, password }) => {
  const level = passwordStrength(password);
  const met = passwordMeetsMinimum(password);
  return <div className="auth-strength" data-level={level}>
    <div className="auth-strength-bar" aria-hidden="true"><span /><span /><span /><span /></div>
    <small id={id} className={met ? "is-met" : ""} aria-live="polite">{met ? `${strengthLabels[level]} password` : "10+ characters with a letter and a number"}</small>
  </div>;
};

// Large status badge at the top of a card: mail sent, success, warning or error.
export const AuthStatus = ({ tone = "info", icon }) => <span className={`auth-status is-${tone}`} aria-hidden="true">
  <span className="auth-status-ring" /><i className={`ti ${icon}`} />
</span>;

// Disables a resend button for a short time after it is used (the API rate-limits as well).
export const useCooldown = (seconds = 60) => {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!remaining) return undefined;
    const timer = window.setTimeout(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [remaining]);
  return [remaining, () => setRemaining(seconds)];
};

const SkeletonBlock = ({ className = "" }) => <span className={`auth-skeleton ${className}`} aria-hidden="true" />;

// Shown while the sign-in pages confirm an existing session with the server.
export const AuthLoadingScreen = ({ label = "Checking your session" }) => <AuthShowcase eyebrow="Secure sign-in" title={<>Welcome to <em>Socialyorix.</em></>} intro="Just a moment while we get things ready.">
  <div className="auth-card-skeleton" role="status" aria-live="polite" aria-label={label}>
    <SkeletonBlock className="is-title" /><SkeletonBlock className="is-line" />
    <SkeletonBlock className="is-label" /><SkeletonBlock className="is-input" />
    <SkeletonBlock className="is-label" /><SkeletonBlock className="is-input" />
    <SkeletonBlock className="is-button" />
    <span className="visually-hidden">{label}…</span>
  </div>
</AuthShowcase>;

// Shown when the session check itself fails (for example the API is unreachable).
export const AuthErrorScreen = ({ message, onRetry }) => <AuthShowcase eyebrow="Secure sign-in" title={<>We couldn't <em>reach Socialyorix.</em></>} intro="Check your connection, then try again.">
  <div className="auth-state">
    <AuthStatus tone="error" icon="ti-plug-connected-x" />
    <h2>Connection problem</h2>
    <p role="alert">{message}</p>
    <button type="button" className="auth-submit" onClick={onRetry}><i className="ti ti-refresh" aria-hidden="true" /> Retry session check</button>
  </div>
</AuthShowcase>;

export default AuthShowcase;
